<template>
    <div class="p-3">
        <div class="d-flex align-center mb-4">
            <v-btn small color="default" class="mr-4" @click="$emit('back')">
                <v-icon left>mdi-arrow-left</v-icon>
                Back
            </v-btn>

            <div class="data-filter-section__title">{{ title }}</div>
            <v-spacer />
        </div>

        <div class="d-flex align-center mb-6">
            <v-switch
                v-model="draft.status"
                :disabled="is_disabled"
                :label="draft.status ? 'Enabled' : 'Disabled'"
                hide-details
                dense
                class="mt-0 d-inline-block"
            />
            <v-spacer />
            <v-btn small class="mr-2" color="default" :disabled="is_disabled" @click="$emit('back')">Close</v-btn>
            <v-btn small color="primary" :disabled="is_disabled || isSaving" @click="onSave">Save</v-btn>
        </div>

        <v-form ref="form" v-model="valid" :disabled="is_disabled">
            <v-row>
                <v-col cols="12" md="6">
                    <v-text-field
                        v-model="draft.label"
                        label="Label"
                        :readonly="is_disabled"
                    />
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" md="6">
                    <div class="data-filter-section">
                        <div class="data-filter-section__title">Filter Type</div>
                        <div class="data-filter-section__desc">
                            Determines whether matching orders are included or excluded
                        </div>

                        <v-radio-group v-model="draft.filterType" row :disabled="is_disabled" class="mt-2">
                            <v-radio label="Include" value="include" />
                            <v-radio label="Exclude" value="exclude" />
                        </v-radio-group>
                    </div>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" md="6">
                    <div class="data-filter-section mt-6">
                        <div class="data-filter-section__title">Hook Type</div>
                        <div class="data-filter-section__desc">Applies before or after getData() processing</div>

                        <v-radio-group v-model="draft.hookType" row :disabled="is_disabled" class="mt-2">
                            <v-radio label="PRE" value="pre" />
                            <v-radio label="POST" value="post" />
                        </v-radio-group>
                    </div>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12">

                    <v-row no-gutters v-for="(group, groupIndex) in draft.conditionGroups" :key="group.id" class="mb-4">
                        <v-col cols="12">
                            <v-card outlined>
                                <v-card-title class="d-flex justify-between align-center">
                                    <span>{{ groupIndex === 0 ? 'Rule Conditions' : 'AND' }}</span>
                                    <v-btn
                                        v-if="draft.conditionGroups.length > 1"
                                        icon
                                        class="ml-auto close-btn"
                                        :disabled="is_disabled"
                                        @click="removeGroup(groupIndex)"
                                    >
                                        <i class="fa fa-times-circle fa-2x"></i>
                                    </v-btn>
                                </v-card-title>

                                <v-card-text>
                                    <div class="subtitle-1 mb-2">Any of the following conditions is true...</div>

                                    <div v-for="(row, rowIndex) in group.rows" :key="row.id" class="d-flex align-center mb-2">
                                        <v-col class="pl-0 pr-1" cols="12" md="4">
                                            <v-text-field
                                                v-model="row.fieldName"
                                                label="Field Name"
                                                :readonly="is_disabled"
                                                dense
                                            />
                                        </v-col>
                                        <v-col class="px-1" cols="12" md="3">
                                            <v-select
                                                v-model="row.operator"
                                                :items="operatorOptions"
                                                :disabled="is_disabled"
                                                dense
                                            >
                                                <template v-slot:label>
                                                    <span>Operator</span>
                                                    <v-tooltip
                                                        v-if="row.operator"
                                                        bottom
                                                        max-width="300"
                                                    >
                                                        <template v-slot:activator="{ on, attrs }">
                                                            <v-icon
                                                                small
                                                                class="ml-1"
                                                                v-bind="attrs"
                                                                v-on="on"
                                                            >
                                                                mdi-information-outline
                                                            </v-icon>
                                                        </template>
                                                        <div>
                                                            <div class="font-weight-bold mb-1">{{ row.operator }}</div>
                                                            <div>{{ getOperatorDescription(row.operator) }}</div>
                                                        </div>
                                                    </v-tooltip>
                                                    <v-chip
                                                        v-if="isRegexOperator(row.operator)"
                                                        x-small
                                                        :color="row.value ? 'primary' : 'grey lighten-1'"
                                                        :disabled="!row.value"
                                                        class="ml-2 regex-chip"
                                                        @click="row.value && openRegex101(row.value)"
                                                    >
                                                        <v-icon x-small left>mdi-open-in-new</v-icon>
                                                        <span class="text-caption">Test Regex</span>
                                                    </v-chip>
                                                </template>
                                                <template v-slot:item="{ item }">
                                                    <v-list-item-content>
                                                        <v-list-item-title>{{ item }}</v-list-item-title>
                                                        <v-list-item-subtitle class="text-caption">{{ getOperatorDescription(item) }}</v-list-item-subtitle>
                                                    </v-list-item-content>
                                                </template>
                                            </v-select>
                                        </v-col>
                                        <v-col class="px-1" cols="12" md="4">
                                            <v-text-field
                                                v-model="row.value"
                                                :label="isListOperator(row.operator) ? 'Value (comma-separated)' : 'Value'"
                                                :readonly="is_disabled"
                                                dense
                                            />
                                        </v-col>
                                        <v-col cols="1" class="d-flex align-center justify-end pl-1 pr-0">
                                            <v-btn
                                                x-small
                                                class="mr-1"
                                                :disabled="is_disabled"
                                                @click="addRow(groupIndex, rowIndex)"
                                            >
                                                +
                                            </v-btn>
                                            <v-btn
                                                x-small
                                                depressed
                                                color="grey lighten-2"
                                                :disabled="is_disabled || group.rows.length === 1"
                                                @click="removeRow(groupIndex, rowIndex)"
                                            >
                                                -
                                            </v-btn>
                                        </v-col>
                                    </div>
                                </v-card-text>
                            </v-card>
                        </v-col>
                    </v-row>

                    <v-btn small :disabled="is_disabled" @click="addGroup">Add condition</v-btn>
                </v-col>
            </v-row>

            <v-row>
                <v-col cols="12" class="d-flex">
                    <v-spacer />
                    <v-btn small class="mr-2" color="default" :disabled="is_disabled" @click="$emit('back')">Close</v-btn>
                    <v-btn small color="primary" :disabled="is_disabled || isSaving" @click="onSave">Save</v-btn>
                </v-col>
            </v-row>
        </v-form>
    </div>
</template>

<script>
import { STANDARD_OPERATORS, OPERATOR_DESCRIPTIONS } from '@/js/constants/operators'

// Static form UI first (fetch-ready).
// Data model matches backend helper semantics:
//   - conditionGroups are AND'ed together
//   - rows inside a group are OR'ed together

const uid = () => `${Date.now()}-${Math.random().toString(16).slice(2)}`

export default {
    props: {
        integrationId: { type: [Number, String], default: null },
        is_disabled: { type: Boolean, default: false },
        isSaving: { type: Boolean, default: false },
        title: { type: String, default: 'Add Data Filter' },
        initialDraft: { type: Object, default: null },
    },

    data() {
        return {
            valid: true,

            operatorOptions: STANDARD_OPERATORS,

            draft: {
                label: '',
                filterType: 'include',
                hookType: 'pre',
                status: true,
                conditionGroups: [
                    {
                        id: uid(),
                        rows: [
                            { id: uid(), fieldName: '', operator: '==', value: '' },
                        ],
                    },
                ],
            },
        }
    },

    mounted() {
        this.applyInitialDraft()
    },

    watch: {
        initialDraft: {
            deep: true,
            handler() {
                this.applyInitialDraft()
            }
        }
    },

    methods: {
        normalizeOperator(op) {
            // Backward compatibility for older saved values.
            // We normalize to the standard operator list casing.
            const map = {
                'equals': '==',
                'not equals': '!=',
                'in list': 'In List',
                'not in list': 'Not In List',
                'contains': 'Contains',
                'not contains': 'Not Contains',
                'is empty': 'Is Empty',
                'is true': 'Is True',
                'is false': 'Is False',
            }

            const key = typeof op === 'string' ? op.trim().toLowerCase() : op
            return map[key] || op
        },

        isListOperator(op) {
            const normalized = this.normalizeOperator(op)
            return normalized === 'In List' || normalized === 'Not In List'
        },

        isRegexOperator(op) {
            const regexOperators = ['regex', 'matches', 'regex match', 'regexp']
            const normalized = typeof op === 'string' ? op.trim().toLowerCase() : ''
            return regexOperators.includes(normalized)
        },

        getRegex101Link(pattern) {
            // Generate regex101.com link with pre-filled pattern
            const encodedPattern = encodeURIComponent(pattern || '')
            const testString = encodeURIComponent('') // Empty test string by default
            
            // Use PHP flavor by default (Laravel uses PCRE)
            return `https://regex101.com/?regex=${encodedPattern}&testString=${testString}&flags=g&flavor=php`
        },

        openRegex101(pattern) {
            if (!pattern) return
            const url = this.getRegex101Link(pattern)
            window.open(url, '_blank', 'noopener,noreferrer')
        },

        getOperatorDescription(operator) {
            return OPERATOR_DESCRIPTIONS[operator] || 'No description available'
        },

        applyInitialDraft() {
            if (!this.initialDraft) {
                return
            }

            // Deep clone so edits don't mutate parent object.
            this.draft = JSON.parse(JSON.stringify(this.initialDraft))

            // Normalize any legacy operator values so v-select shows the correct selected item.
            this.draft.conditionGroups = (this.draft.conditionGroups || []).map((g) => ({
                ...g,
                rows: (g.rows || []).map((r) => ({
                    ...r,
                    operator: this.normalizeOperator(r.operator),
                })),
            }))
        },

        addGroup() {
            this.draft.conditionGroups.push({
                id: uid(),
                rows: [{ id: uid(), fieldName: '', operator: '==', value: '' }],
            })
        },

        removeGroup(groupIndex) {
            this.draft.conditionGroups.splice(groupIndex, 1)
        },

        addRow(groupIndex, rowIndex) {
            this.draft.conditionGroups[groupIndex].rows.splice(rowIndex + 1, 0, {
                id: uid(),
                fieldName: '',
                operator: '==',
                value: '',
            })
        },

        removeRow(groupIndex, rowIndex) {
            this.draft.conditionGroups[groupIndex].rows.splice(rowIndex, 1)
        },

        onSave() {
            // Static UI: just emit draft for now.
            // Later: POST /api/integration-data-filters with normalized payload.
            this.$emit('save', this.draft)
        },
    },
}
</script>

<style scoped>
.data-filter-section {
    padding: 0;
}

/* Match Rule Conditions v-card-title sizing */
.data-filter-section__title {
    font-size: 20px;
    font-weight: 500;
    line-height: 1.4;
    margin-bottom: 4px;
}

/* Match "Any of the following conditions is true..." tone */
.data-filter-section__desc {
    color: rgba(0, 0, 0, 0.6);
    font-size: 16px;
    margin-bottom: 0;
}

.regex-chip {
    cursor: pointer;
    
    &.v-chip--disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}
</style>
